var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cx, css as emotionCss } from 'emotion';
import { getChildByType } from 'react-nanny';
import { enableFormSubmit, inputChange, preventFormSubmit, useCitrusForm, } from '@citrus/form';
import { Input, styles, citronStyles, Styled, validationAccessibility, CharacterCount, } from '@citrus/input';
import { getAutomation, getDebug, useDebounce, useReadSessionStorage, getStyleNames, useFormInputId, useThrottle, useUpdateValidation, useUserManagedValidation, useValidationOnMount, } from '@citrus/runtime';
import { useTheme } from '@citrus/user';
import { at, downKeyPressed, enterKeyPressed, isNilOrEmpty, isNotNil, isNotNilOrEmpty, isString, only, take, upKeyPressed, } from '@pcty/utils';
import { AutoComplete, AutoCompleteItem, AutoCompletePreview, IconContainer, IconContainerDisabled, InputContainer, ReadOnly, } from './Textfield.styled';
import { AutoCompleteResults } from './private/AutoCompleteResults';
import { ClearButton } from './private/ClearButton';
import { usePerformanceTimeout } from './private/usePerformanceTimeout';
import { autocompleteItemFocus, iconPadding } from '../styles/legacy';
import { autocompleteItemFocus as autoCompleteItemFocusCitron, autocompletePreviewIconPadding, iconPadding as iconPaddingCitron, } from '../styles/citron';
var InputStyled = Styled.Input;
var disabledStyles = styles.disabled, error = styles.error;
var disabledCitronStyles = citronStyles.disabled, errorCitron = citronStyles.error;
var VERSION = '7.6.1';
var Textfield = React.forwardRef(function (_a, ref) {
    var _b;
    var ariaLabel = _a.ariaLabel, _c = _a.autocomplete, autocomplete = _c === void 0 ? false : _c, autocompleteOptions = _a.autocompleteOptions, _d = _a.autocompletePortal, autocompletePortal = _d === void 0 ? false : _d, _e = _a.autoFocus, autoFocus = _e === void 0 ? false : _e, automationId = _a.automationId, _f = _a.characterCountOverflow, characterCountOverflow = _f === void 0 ? false : _f, className = _a.className, _g = _a.classNameWrapper, classNameWrapper = _g === void 0 ? '' : _g, clearable = _a.clearable, clearOnFocus = _a.clearOnFocus, css = _a.css, disabled = _a.disabled, _h = _a.emptyLabel, emptyLabel = _h === void 0 ? false : _h, errorMessage = _a.errorMessage, footnote = _a.footnote, formatter = _a.formatter, _j = _a.hideOptionalLabel, hideOptionalLabel = _j === void 0 ? false : _j, id = _a.id, label = _a.label, labelAddOn = _a.labelAddOn, labelPosition = _a.labelPosition, maxLength = _a.maxLength, onBlur = _a.onBlur, onChange = _a.onChange, onFocus = _a.onFocus, _k = _a.performance, performance = _k === void 0 ? 'debounce' : _k, performanceTimeout = _a.performanceTimeout, _l = _a.placeholder, placeholder = _l === void 0 ? '' : _l, _m = _a.preventAutoFill, preventAutoFill = _m === void 0 ? false : _m, _o = _a.readOnly, readOnly = _o === void 0 ? false : _o, renderIcon = _a.renderIcon, _p = _a.required, required = _p === void 0 ? false : _p, _q = _a.showCharacterCount, showCharacterCount = _q === void 0 ? false : _q, _r = _a.showRequiredLabel, showRequiredLabel = _r === void 0 ? false : _r, showValidationState = _a.showValidationState, tooltip = _a.tooltip, _s = _a.type, type = _s === void 0 ? 'text' : _s, valid = _a.valid, validate = _a.validate, _t = _a.value, value = _t === void 0 ? '' : _t, wrapperStyles = _a.wrapperStyles, inputRef = _a.inputRef, _u = _a.__TYPE, __TYPE = _u === void 0 ? 'Textfield' : _u, other = __rest(_a, ["ariaLabel", "autocomplete", "autocompleteOptions", "autocompletePortal", "autoFocus", "automationId", "characterCountOverflow", "className", "classNameWrapper", "clearable", "clearOnFocus", "css", "disabled", "emptyLabel", "errorMessage", "footnote", "formatter", "hideOptionalLabel", "id", "label", "labelAddOn", "labelPosition", "maxLength", "onBlur", "onChange", "onFocus", "performance", "performanceTimeout", "placeholder", "preventAutoFill", "readOnly", "renderIcon", "required", "showCharacterCount", "showRequiredLabel", "showValidationState", "tooltip", "type", "valid", "validate", "value", "wrapperStyles", "inputRef", "__TYPE"]);
    var _performanceTimeout = usePerformanceTimeout(performance, performanceTimeout);
    var _v = useCitrusForm(), form = _v.form, dispatch = _v.dispatch, customValidate = _v.validate, formDisabled = _v.disabled;
    var debug = useReadSessionStorage('citrus-debug', 'false');
    var isCitron = useTheme().citrus2022;
    var _w = useState(), event = _w[0], setEvent = _w[1];
    var isRequired = isString(required) ? true : required !== null && required !== void 0 ? required : false;
    var requiredMessage = isString(required) ? required : undefined;
    var maxLengthValue = characterCountOverflow ? -1 : maxLength;
    var _renderIcon = function () {
        if (isNilOrEmpty(renderIcon))
            return;
        var icon = getChildByType(renderIcon === null || renderIcon === void 0 ? void 0 : renderIcon(), ['Icon']);
        var Container = disabled ? IconContainerDisabled : IconContainer;
        return isNilOrEmpty(icon) ? undefined : React.createElement(Container, null, icon);
    };
    var _renderClear = function () {
        if (clearable && !readOnly && !disabled && isNotNilOrEmpty(value)) {
            return (React.createElement(ClearButton, { automationId: automationId, disabled: disabled !== null && disabled !== void 0 ? disabled : formDisabled, focus: focus, id: id, onClick: function (e) {
                    eventType.current = 'clear';
                    var validation = getValidation('');
                    lastEmittedValue.current = '';
                    setValidation(only(validation, ['valid', 'errorMessage']));
                    onChange === null || onChange === void 0 ? void 0 : onChange({
                        id: id,
                        value: '',
                        eventType: 'clear',
                        forceNotify: false,
                        autocompleted: false,
                        validation: validation,
                        e: e,
                    });
                    if (!userManagedValidation) {
                        updateForm(formInputId, __assign(__assign({}, validation), { value: '', id: id, label: label, dirty: true }));
                    }
                } }));
        }
    };
    // #region state
    var _x = useState(''), inputValue = _x[0], setInputValue = _x[1];
    var _y = useState({ valid: false }), _validation = _y[0], setValidation = _y[1];
    var _z = useState(autoFocus), focus = _z[0], setFocus = _z[1];
    var _0 = useState(-1), autoCompleteFocusIndex = _0[0], setAutoCompleteFocusIndex = _0[1];
    var autoCompleteItemSelected = useRef(false);
    var eventType = useRef();
    var lastEmittedValue = useRef(value);
    var autoCompleteRef = useRef(null);
    var autoCompleteMeasureRef = useRef(null);
    var formInputId = useFormInputId(id);
    // casting is safe because hook handles undefined possibility, but it's typed as required
    var userManagedValidation = useUserManagedValidation(valid, errorMessage, showValidationState, setValidation);
    // #endregion state
    var icon = _renderIcon();
    var clear = _renderClear();
    // #region callbacks
    // eslint-disable-next-line react-hooks/exhaustive-deps
    var updateForm = useCallback(inputChange(dispatch, customValidate), [customValidate]);
    var validateRequired = useCallback(function (value) {
        if (isNilOrEmpty(value)) {
            return {
                valid: false,
                errorMessage: requiredMessage !== null && requiredMessage !== void 0 ? requiredMessage : "".concat(label !== null && label !== void 0 ? label : 'This', " is required"),
            };
        }
        return { valid: true };
    }, [label, requiredMessage]);
    var getValidation = useCallback(function (value) {
        if (userManagedValidation && !validate)
            return { valid: valid, errorMessage: errorMessage };
        var validation = { valid: true };
        if (isRequired) {
            validation = validateRequired(value);
        }
        if (validate && validation.valid) {
            validation = validate(value, {
                id: id,
                label: label,
                required: isRequired,
            });
        }
        return validation;
    }, [errorMessage, id, isRequired, label, userManagedValidation, valid, validate, validateRequired]);
    /** Event handler for when the user hits the enter key while focus is inside the textfield or directional button with autocomplete */
    var onKeyPress = useCallback(function (e) {
        var _a, _b;
        if (autocomplete && autoCompleteFocusIndex >= 0 && autoCompleteFocusIndex < 6 && enterKeyPressed(e)) {
            // Emit onChange here with the newly selected autocomplete value
            // Stop propagation to prevent a form from submitting
            e.preventDefault();
            var value_1 = at(autocompleteOptions, autoCompleteFocusIndex, '');
            var validation = getValidation(value_1);
            lastEmittedValue.current = value_1;
            setValidation(only(validation, ['valid', 'errorMessage']));
            setAutoCompleteFocusIndex(-1);
            autoCompleteItemSelected.current = true;
            onChange === null || onChange === void 0 ? void 0 : onChange({
                id: id,
                value: value_1,
                eventType: eventType.current,
                forceNotify: false,
                autocompleted: true,
                validation: validation,
                e: e,
            });
            if (!userManagedValidation) {
                updateForm(formInputId, __assign(__assign({}, validation), { value: value_1, id: id, label: label, dirty: true }));
            }
            enableFormSubmit(dispatch)();
        }
        if (enterKeyPressed(e)) {
            e.target.blur();
        }
        // autocomplete must have a length between 0 and 6
        var autoCompleteLength = ((_a = autocompleteOptions === null || autocompleteOptions === void 0 ? void 0 : autocompleteOptions.length) !== null && _a !== void 0 ? _a : 0) > 6 ? 6 : (_b = autocompleteOptions === null || autocompleteOptions === void 0 ? void 0 : autocompleteOptions.length) !== null && _b !== void 0 ? _b : 0;
        if (isNotNilOrEmpty(autocompleteOptions) && downKeyPressed(e)) {
            setAutoCompleteFocusIndex(autoCompleteFocusIndex + 1 < autoCompleteLength ? autoCompleteFocusIndex + 1 : 0);
        }
        if (isNotNilOrEmpty(autocompleteOptions) && upKeyPressed(e)) {
            setAutoCompleteFocusIndex(autoCompleteFocusIndex - 1 >= 0 ? autoCompleteFocusIndex - 1 : autoCompleteLength - 1);
        }
    }, [
        autoCompleteFocusIndex,
        autocomplete,
        autocompleteOptions,
        dispatch,
        formInputId,
        getValidation,
        id,
        label,
        onChange,
        updateForm,
        userManagedValidation,
    ]);
    var onClickAutoCompleteItem = useCallback(function (e, item) { return function () {
        enableFormSubmit(dispatch)();
        var validation = getValidation(item);
        lastEmittedValue.current = item;
        setValidation(only(validation, ['valid', 'errorMessage']));
        setAutoCompleteFocusIndex(-1);
        autoCompleteItemSelected.current = true;
        onChange === null || onChange === void 0 ? void 0 : onChange({
            id: id,
            value: item,
            eventType: eventType.current,
            forceNotify: false,
            autocompleted: true,
            validation: validation,
            e: e,
        });
        if (!userManagedValidation) {
            updateForm(formInputId, __assign(__assign({}, validation), { value: item, id: id, label: label, dirty: true }));
        }
    }; }, [dispatch, formInputId, getValidation, id, label, onChange, updateForm, userManagedValidation]);
    // #endregion callbacks
    // #region effects
    // casting is safe becuase hook handles undefined possibility, but it's typed as required
    useValidationOnMount(id, label, value, userManagedValidation, getValidation, setValidation, updateForm);
    useUpdateValidation(form, formInputId, valid, errorMessage, _validation, setValidation);
    useEffect(function () { return setInputValue(formatter && isNilOrEmpty(eventType.current) ? formatter(value) : value); }, [value, formatter]);
    useEffect(function () {
        if (isNotNilOrEmpty(autocompleteOptions) && !(form === null || form === void 0 ? void 0 : form.preventSubmit)) {
            preventFormSubmit(dispatch)();
        }
    }, [autocompleteOptions, dispatch, form === null || form === void 0 ? void 0 : form.preventSubmit]);
    // if required changes after mount, revalidate
    useEffect(function () {
        var _a, _b, _c;
        var validation = getValidation(value);
        setValidation(validation);
        updateForm(formInputId, __assign(__assign({}, validation), { value: value, id: id, label: label, dirty: (_c = (_b = (_a = form === null || form === void 0 ? void 0 : form.inputs) === null || _a === void 0 ? void 0 : _a[formInputId]) === null || _b === void 0 ? void 0 : _b.dirty) !== null && _c !== void 0 ? _c : false }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [required]);
    // unmount
    useEffect(function () { return enableFormSubmit(dispatch)(); }, [dispatch]);
    // #endregion effects
    useDebounce(function () {
        if (performance !== 'debounce')
            return;
        if (event && eventType.current === 'change') {
            var validation = getValidation(inputValue);
            lastEmittedValue.current = inputValue;
            setValidation(only(validation, ['valid', 'errorMessage']));
            onChange === null || onChange === void 0 ? void 0 : onChange({
                id: id,
                value: inputValue,
                eventType: eventType.current,
                forceNotify: false,
                autocompleted: false,
                validation: validation,
                e: event,
            });
            if (!userManagedValidation) {
                updateForm(formInputId, __assign(__assign({}, validation), { value: inputValue, id: id, label: label, dirty: true }));
            }
        }
    }, _performanceTimeout, [inputValue, eventType.current]);
    var _onThrottle = useCallback(function (inputValue) {
        if (performance === 'debounce' ||
            autoCompleteItemSelected.current ||
            inputValue === value ||
            lastEmittedValue.current === inputValue)
            return;
        if (event && eventType.current === 'change') {
            var validation = getValidation(inputValue);
            lastEmittedValue.current = inputValue;
            setValidation(only(validation, ['valid', 'errorMessage']));
            onChange === null || onChange === void 0 ? void 0 : onChange({
                id: id,
                value: inputValue,
                eventType: eventType.current,
                forceNotify: false,
                autocompleted: false,
                validation: validation,
                e: event,
            });
            if (!userManagedValidation) {
                updateForm(formInputId, __assign(__assign({}, validation), { value: inputValue, id: id, label: label, dirty: true }));
            }
        }
    }, [event, formInputId, getValidation, id, label, onChange, performance, updateForm, userManagedValidation, value]);
    useThrottle(inputValue, _onThrottle, _performanceTimeout);
    var handleInputChange = function (e) {
        setInputValue(e.target.value);
        autoCompleteItemSelected.current = false;
        setEvent(e);
        eventType.current = 'change';
        if (!focus) {
            handleFocus(e);
        }
    };
    var handleBlur = function (e) {
        var executeBlur = function (selected) { return function () {
            enableFormSubmit(dispatch)();
            setFocus(false);
            setAutoCompleteFocusIndex(-1);
            var value = formatter ? formatter(e.target.value) : e.target.value;
            if (onBlur) {
                onBlur({ id: id, value: value });
            }
            if (selected || lastEmittedValue.current === value) {
                /*
                  NOTE: This timeout is required so that the component will know that an autocomplete item has been selected
                        for long enough for a screen reader to identify that there is a status message to read. Once it's
                        been identified by the screen reader, we can remove it even before it's finished reading.
                */
                setTimeout(function () {
                    autoCompleteItemSelected.current = false;
                }, 50);
                return;
            }
            eventType.current = 'blur';
            setInputValue(value);
            var validation = getValidation(value);
            lastEmittedValue.current = value;
            setValidation(only(validation, ['valid', 'errorMessage']));
            onChange === null || onChange === void 0 ? void 0 : onChange({
                id: id,
                value: value,
                eventType: eventType.current,
                forceNotify: lastEmittedValue.current !== value,
                autocompleted: false,
                validation: validation,
                e: e,
            });
            if (!userManagedValidation) {
                updateForm(formInputId, __assign(__assign({}, validation), { value: value, id: id, label: label, dirty: true }));
            }
        }; };
        if (isNotNilOrEmpty(autocompleteOptions)) {
            setTimeout(executeBlur(autoCompleteItemSelected.current), 250);
            return;
        }
        if (clearable && isNotNilOrEmpty(value)) {
            return;
        }
        executeBlur(autoCompleteItemSelected.current)();
    };
    var handleFocus = function (e) {
        if (isNotNilOrEmpty(autocompleteOptions) && !(form === null || form === void 0 ? void 0 : form.preventSubmit)) {
            preventFormSubmit(dispatch)();
        }
        setFocus(true);
        if (onFocus) {
            onFocus({ id: id, e: e });
        }
        if (clearOnFocus) {
            var validation = getValidation(inputValue);
            lastEmittedValue.current = inputValue;
            setValidation(only(validation, ['valid', 'errorMessage']));
            onChange === null || onChange === void 0 ? void 0 : onChange({
                id: id,
                value: '',
                eventType: 'focus',
                forceNotify: false,
                autocompleted: false,
                validation: validation,
                e: e,
            });
            if (!userManagedValidation) {
                updateForm(formInputId, __assign(__assign({}, validation), { value: '', id: id, label: label, dirty: true }));
            }
        }
    };
    // #region styles
    var disabledStylesCss = isCitron ? disabledCitronStyles : disabledStyles;
    var errorStylesCss = isCitron ? errorCitron : error;
    var emotionObj = (_b = {},
        _b[emotionCss(css)] = isNotNilOrEmpty(css),
        _b[emotionCss(errorStylesCss)] = !_validation.valid && (showValidationState || (form === null || form === void 0 ? void 0 : form.showValidationState)),
        _b[emotionCss(disabledStylesCss)] = disabled !== null && disabled !== void 0 ? disabled : formDisabled,
        _b[emotionCss(iconPadding)] = !isCitron && isNotNilOrEmpty(icon),
        _b[emotionCss(iconPaddingCitron)] = isCitron && isNotNilOrEmpty(icon),
        _b);
    var classes = cx(emotionObj, className || '');
    var styleNames = {
        error: emotionObj[emotionCss(errorStylesCss)],
        disabled: emotionObj[emotionCss(disabledStylesCss)],
        iconPadding: emotionObj[emotionCss(iconPadding)] || emotionObj[emotionCss(iconPaddingCitron)],
    };
    // #endregion styles
    var readOnlyClasses = isNotNilOrEmpty(css) ? cx(emotionCss(css)) : undefined;
    var showAutoComplete = focus && isNotNil(autocompleteOptions) && !autoCompleteItemSelected.current;
    var renderAutoComplete = function () {
        if (!showAutoComplete)
            return;
        var css = isCitron ? autoCompleteItemFocusCitron : autocompleteItemFocus;
        var template = (React.createElement(React.Fragment, null,
            React.createElement(AutoComplete, __assign({}, getAutomation(automationId, 'input-select-dropdown-list-container'), { id: id + '-autocomplete-list', ref: autoCompleteRef, role: "listbox" }), take(autocompleteOptions, 6).map(function (item, index) { return (React.createElement(AutoCompleteItem, { "aria-posinset": index + 1, "aria-selected": autoCompleteFocusIndex === index, "aria-setsize": take(autocompleteOptions, 6).length, css: autoCompleteFocusIndex === index ? css : undefined, id: "".concat(id, "-autocomplete-item-").concat(index), key: "".concat(id, "-autocomplete-item-").concat(index), onClick: onClickAutoCompleteItem({}, item), role: "option", tabIndex: "-1" }, item)); })),
            autoCompleteFocusIndex >= 0 && autoCompleteFocusIndex < 6 && (React.createElement(AutoCompletePreview, { css: isNotNilOrEmpty(icon) ? autocompletePreviewIconPadding : undefined }, at(autocompleteOptions, autoCompleteFocusIndex, '')))));
        if (!autocompletePortal) {
            return template;
        }
        return createPortal(template, document.body);
    };
    var autoCompleteProps = autocomplete
        ? {
            'aria-activedescendant': autoCompleteFocusIndex >= 0 ? "".concat(id, "-autocomplete-item-").concat(autoCompleteFocusIndex) : undefined,
            'aria-expanded': showAutoComplete,
            'aria-autocomplete': 'list',
            'aria-controls': id + '-autocomplete-list',
            autoComplete: id + new Date().getTime().toString(),
            role: 'combobox',
        }
        : {};
    var _showValidationState = showValidationState !== undefined ? showValidationState : form === null || form === void 0 ? void 0 : form.showValidationState;
    return (React.createElement(Input, { automationId: automationId, className: classNameWrapper, css: wrapperStyles, disabled: disabled !== null && disabled !== void 0 ? disabled : formDisabled, emptyLabel: emptyLabel, errorMessage: _validation.errorMessage, footnote: footnote, hideOptionalLabel: hideOptionalLabel, id: id, label: label, labelAddOn: labelAddOn, labelPosition: labelPosition, readOnly: readOnly, ref: ref, renderCharacterCount: function () {
            return showCharacterCount ? React.createElement(CharacterCount, { automationId: automationId, max: maxLength, text: "".concat(value) }) : null;
        }, required: isRequired, showRequiredLabel: showRequiredLabel, showValidationState: _showValidationState, tooltip: tooltip, valid: _validation.valid, wrapChildren: true }, readOnly ? (React.createElement(ReadOnly, { className: readOnlyClasses, id: id }, value)) : (React.createElement(InputContainer, { ref: autoCompleteMeasureRef },
        icon,
        React.createElement(AutoCompleteResults, { autocomplete: autocomplete, count: autocompleteOptions && (autocompleteOptions === null || autocompleteOptions === void 0 ? void 0 : autocompleteOptions.length) >= 0
                ? take(autocompleteOptions, 6).length
                : 0, id: id, open: showAutoComplete, selected: autoCompleteItemSelected.current, value: value }),
        React.createElement(InputStyled, __assign({}, (preventAutoFill ? { autoComplete: id + new Date().getTime() } : {}), other, getDebug(debug === 'true', __TYPE, VERSION), getAutomation(automationId), (debug ? { 'data-style': getStyleNames(styleNames) } : {}), validationAccessibility(id, _validation.valid, _showValidationState !== null && _showValidationState !== void 0 ? _showValidationState : false), autoCompleteProps, { "aria-label": ariaLabel, autoFocus: autoFocus, className: classes, disabled: disabled !== null && disabled !== void 0 ? disabled : formDisabled, id: id, maxLength: maxLengthValue, onBlur: handleBlur, onChange: handleInputChange, onFocus: handleFocus, onKeyUp: onKeyPress, placeholder: placeholder, readOnly: readOnly, ref: inputRef, required: isRequired, type: type, value: inputValue })),
        renderAutoComplete(),
        clear))));
});
Textfield.defaultProps = {
    __TYPE: 'Textfield',
};
export default Textfield;
